/**
 * @fileoverview Authentication API client module
 *
 * Handles backend authentication including:
 * - Profile sync (exchanges Okta token for internal session token)
 * - Local-only login for development
 * - User profile retrieval
 * - Session refresh
 * - Logout audit logging
 */

import axios from 'axios'
import apiClient, { resolveApiBaseUrl } from '@/api/client'

/**
 * @typedef {Object} UserProfile
 * @property {string} id - User ID
 * @property {string} email - User email
 * @property {string} display_name - User display name
 * @property {string} role - User role (admin, instructor, student)
 * @property {boolean} is_active - Whether user is active
 * @property {string} created_at - ISO timestamp of creation
 * @property {string} [updated_at] - ISO timestamp of last update
 */

/**
 * @typedef {Object} LogoutResponse
 * @property {string} message - Logout success message
 */

/**
 * Sync user profile with backend
 *
 * Validates Okta access token and creates/updates user in backend database.
 * Returns internal session token that should be used for subsequent API calls.
 *
 * @param {string} oktaAccessToken - Okta access token from Okta SDK
 * @returns {Promise<{access_token: string, token_type: string, expires_in: number, user: UserProfile}>} Auth response with internal session token
 * @example
 * const authResponse = await syncProfile(oktaToken)
 * console.debug(authResponse.access_token, authResponse.user.email)
 */
export async function syncProfile(oktaAccessToken) {
  const baseURL = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL)
  console.debug(
    '[Auth API] syncProfile: Calling /auth/sync with Okta token (first 30 chars):',
    oktaAccessToken.substring(0, 30) + '...'
  )

  const response = await axios.post(
    `${baseURL}/v1/auth/sync`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${oktaAccessToken}`
      }
    }
  )

  console.debug('[Auth API] syncProfile: Response status:', response.status)
  console.debug('[Auth API] syncProfile: Response data:', {
    hasAccessToken: !!response.data.access_token,
    accessTokenPrefix: response.data.access_token
      ? response.data.access_token.substring(0, 30) + '...'
      : null,
    tokenType: response.data.token_type,
    expiresIn: response.data.expires_in,
    userEmail: response.data.user?.email
  })

  return response.data
}

/**
 * Log in with a deterministic local-only role.
 *
 * @param {'admin'|'instructor'|'user'} role - Local-only role to assume
 * @returns {Promise<{access_token: string, token_type: string, expires_in: number, user: UserProfile}>}
 */
export async function localLogin(role) {
  const baseURL = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL)
  const response = await axios.post(`${baseURL}/v1/auth/local-login`, { role }, {
    headers: {
      'Content-Type': 'application/json'
    }
  })
  return response.data
}

/**
 * Get current authenticated user profile
 *
 * @param {string} sessionToken - Internal session token
 * @returns {Promise<UserProfile>} Current user data
 * @example
 * const user = await getMe(token)
 * console.debug(user.email, user.role)
 */
export async function getMe(sessionToken) {
  const baseURL = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL)
  const response = await axios.get(`${baseURL}/v1/auth/me`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionToken}`
    }
  })
  return response.data
}

/**
 * Logout current user
 *
 * Notifies backend of logout event for audit logging.
 * Actual logout from Okta is handled by Okta SDK.
 *
 * @param {string} sessionToken - Internal session token
 * @returns {Promise<LogoutResponse>} Logout confirmation
 * @example
 * await logout(token)
 * // Then call oktaAuth.signOut() to complete logout
 */
export async function logout(sessionToken) {
  const baseURL = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL)
  const response = await axios.post(
    `${baseURL}/v1/auth/logout`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionToken}`
      }
    }
  )
  return response.data
}

/**
 * Refresh the current internal session token.
 *
 * Uses a raw axios client so auth refresh logic does not recurse through the shared apiClient.
 *
 * @param {string} sessionToken - Current internal session token
 * @returns {Promise<{access_token: string, token_type: string, expires_in: number, user: UserProfile}>}
 */
export async function refreshSession(sessionToken) {
  const baseURL = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL)
  const response = await axios.post(
    `${baseURL}/v1/auth/refresh`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionToken}`
      }
    }
  )
  return response.data
}

/**
 * Update current user's preferences
 *
 * Uses the authenticated apiClient so the session token is attached automatically.
 *
 * @param {Object} preferences - Preference keys to update (shallow merge)
 * @param {Object} [preferences.notifications] - Notification preferences
 * @param {Object} [preferences.display] - Display preferences
 * @param {string} [preferences.theme] - Theme preference
 * @returns {Promise<UserProfile>} Updated user profile with merged preferences
 * @example
 * const user = await updatePreferences({ theme: 'dark', notifications: { email: false } })
 */
export async function updatePreferences(preferences) {
  const response = await apiClient.patch('/v1/auth/me/preferences', preferences)
  return response.data
}
